import { t } from '@bia-auction/shared-i18n';
import type {
  BodyType,
  EquipmentKind,
  ExpertisePart,
  ExpertiseState,
  Fuel,
  Gear,
  VehicleCondition,
  VehicleType,
} from '@bia-auction/shared-schema';

/**
 * Enum → label for the M11 vehicle surfaces. The enum members are the *legacy* identifiers
 * (see `packages/shared-schema/src/vehicle.ts`), so a key is a stable data value and the label
 * is presentation — never the other way round. Turkish wording comes from the legacy locale
 * file so operators read the words they already know.
 *
 * Equipment NAMES are deliberately absent: they are rows in `equipment_definitions`
 * (`nameTr`/`nameEn`), not keys, so adding one is not a code change (ADR 0025).
 */

export const vehicleTypeLabel = (v: VehicleType) => t(`vehicleType.${v}`);
export const fuelLabel = (v: Fuel) => t(`fuel.${v}`);
export const gearLabel = (v: Gear) => t(`gear.${v}`);
export const bodyTypeLabel = (v: BodyType) => t(`bodyType.${v}`);
export const conditionLabel = (v: VehicleCondition) => t(`vehicleCondition.${v}`);
export const expertisePartLabel = (v: ExpertisePart) => t(`expertise.part.${v}`);
export const expertiseStateLabel = (v: ExpertiseState) => t(`expertise.state.${v}`);
export const equipmentKindLabel = (v: EquipmentKind) => t(`equipmentKind.${v}`);

/**
 * The human name of a vehicle: `Brand Series Model`, falling back to the plate when nothing
 * is filled in yet. A car with no brand still has to be identifiable in a list.
 */
export function vehicleName(v: {
  brand: string | null;
  series?: string | null;
  modelName: string | null;
  plate?: string | null;
}): string {
  const parts = [v.brand, v.series, v.modelName].filter((p): p is string => !!p && p.length > 0);
  return parts.length > 0 ? parts.join(' ') : (v.plate ?? '—');
}
